import type {
  CalendarClient,
  CalendarEvent,
  CalendarParticipationStatus,
} from '@wtfalch/calendar/calendar';
import { Button, Icon } from '@wtfalch/design';
import { useEffect, useState } from 'react';

import { eventTimeLabel } from '../calendar/layout.ts';
import { isOrganiser, myParticipant } from '../calendar/participants.ts';
import { durationInMinutes } from './counter.ts';
import { displayName } from './format.ts';

const ANSWERS: readonly { status: CalendarParticipationStatus; label: string }[] = [
  { status: 'accepted', label: 'Yes' },
  { status: 'tentative', label: 'Maybe' },
  { status: 'declined', label: 'No' },
];

/**
 * `Mail-invitation.dc.html`: the invitation a thread carries, answered in
 * place. `event` is the copy on my own calendars that the `.ics` names; the
 * card stands in for the bare attachment only when I am a guest on it, never
 * its organiser, since "Going?" is a guest's question. The answer goes
 * through `respond`, the guest's verb, which patches only my own entry.
 *
 * `onAnswered` hands back the event as it now stands, so the caller's cache
 * does not show the old answer on the next open.
 */
export function InvitationCard({
  calendarClient,
  event,
  onAnswered,
}: {
  calendarClient: CalendarClient;
  event: CalendarEvent;
  onAnswered: (event: CalendarEvent) => void;
}) {
  const [guest, setGuest] = useState<'loading' | 'no' | { calendarAddress: string }>('loading');
  const [status, setStatus] = useState<CalendarParticipationStatus>('needs-action');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Keyed on the event's own id: a refetch of the thread hands this a new
  // object for the same event, and that must not reset a chosen answer.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see above
  useEffect(() => {
    let live = true;
    calendarClient
      .identity()
      .then((identity) => {
        if (!live) return;
        const mine = myParticipant(event, identity);
        if (!mine || isOrganiser(mine)) {
          setGuest('no');
          return;
        }
        setStatus(mine.participationStatus ?? 'needs-action');
        setGuest({ calendarAddress: mine.calendarAddress });
      })
      .catch(() => {
        if (live) setGuest('no');
      });
    return () => {
      live = false;
    };
  }, [calendarClient, event.id]);

  if (guest === 'loading' || guest === 'no') return null;
  const { calendarAddress } = guest;

  const answer = async (next: CalendarParticipationStatus) => {
    if (busy || next === status) return;
    setBusy(true);
    setError(null);
    try {
      await calendarClient.respond(event.id, next);
      setStatus(next);
      onAnswered({
        ...event,
        participants: (event.participants ?? []).map((p) =>
          p.calendarAddress === calendarAddress ? { ...p, participationStatus: next } : p,
        ),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send your answer. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const start = new Date(event.start);
  const end = new Date(start.getTime() + durationInMinutes(event.duration) * 60_000);
  const [day = '', range = ''] = eventTimeLabel(start, end).split(' · ');
  const organiser = event.participants?.find(isOrganiser);
  const from = organiser
    ? displayName({ name: organiser.name, email: organiser.calendarAddress })
    : null;

  return (
    <div className="card w-full mail-counter mail-invite">
      <div className="flex items-center gap-3 mail-counter-head">
        <span className="flex text-info">
          <Icon name="calendar" size={18} />
        </span>
        <span className="flex flex-col grow min-w-0 gap-0.5">
          <span className="text-sm font-strong">{event.title}</span>
          <span className="text-xs text-muted">
            {from ? `${from} invited you.` : 'You are invited.'}
          </span>
        </span>
      </div>
      <div className="flex flex-col gap-1 mail-counter-times">
        <span className="h3">{day}</span>
        <span className="text-sm text-muted">{range}</span>
      </div>
      {error && (
        <p className="text-sm text-bad m-0 mail-counter-note" role="alert">
          {error}
        </p>
      )}
      <div className="flex items-center gap-2 mail-counter-foot">
        <span className="text-sm font-strong grow">Going?</span>
        {ANSWERS.map(({ status: value, label }) => (
          <Button
            key={value}
            size="sm"
            kind={status === value ? 'primary' : 'default'}
            aria-pressed={status === value}
            busy={busy}
            onPress={() => void answer(value)}
          >
            {label}
          </Button>
        ))}
      </div>
    </div>
  );
}
